import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Brush, Images, Refrigerator, Camera } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { playSound, SOUNDS } from '../utils/sounds';
import { CB_SPRING_SOFT } from '../utils/animations';
import { useSectionTheme } from '../hooks/useSectionTheme';

const sideItems = [
  { path: '/', icon: Home, labelKey: 'home' },
  {
    path: '/mur',
    icon: Images,
    labelKey: 'wall',
    matches: (p) => p === '/mur' || p === '/showcase/coloring-of-the-month',
  },
];

const tailItems = [
  {
    path: '/studio',
    icon: Brush,
    labelKey: 'studio',
    matches: (p) =>
      p === '/studio'
      || p.startsWith('/studio/')
      || p === '/palettes'
      || p === '/light-helper'
      || p === '/textures'
      || p.startsWith('/textures/'),
  },
  {
    path: '/gallery',
    icon: Refrigerator,
    labelKey: 'fridge',
    matches: (p) => p === '/gallery',
  },
];

export default function BottomNav({ t }) {
  const navigate = useNavigate();
  const location = useLocation();
  const reducedMotion = useReducedMotion();
  const sectionTheme = useSectionTheme();

  const handleNavigate = (path) => {
    playSound(SOUNDS.CLICK);
    navigate(path);
  };

  const isActive = (item) =>
    typeof item.matches === 'function'
      ? item.matches(location.pathname)
      : location.pathname === item.path;

  const renderTab = (item) => {
    const { path, icon: Icon, labelKey } = item;
    const active = isActive(item);
    // L'onglet actif consomme les tokens de section. Les classes Tailwind
    // de couleur active sont retirees - on garde uniquement la base
    // structurelle, les transitions et l'etat inactif.
    const activeStyle = active
      ? {
          color: sectionTheme.text,
          background: sectionTheme.bg,
          transition: 'color 280ms var(--cb-ease-out), background 280ms var(--cb-ease-out)',
        }
      : { transition: 'color 280ms var(--cb-ease-out), background 280ms var(--cb-ease-out)' };
    return (
      <motion.button
        key={path}
        onClick={() => handleNavigate(path)}
        className={`relative flex flex-col items-center justify-center gap-1 py-2 px-2 rounded-2xl min-h-[48px] ${
          active
            ? ''
            : 'text-[color:var(--cb-text-muted)] opacity-[0.55] hover:opacity-100 hover:text-[color:var(--cb-text)]'
        }`}
        style={activeStyle}
        whileTap={reducedMotion ? undefined : { scale: 0.92 }}
        transition={reducedMotion ? { duration: 0 } : CB_SPRING_SOFT}
        data-testid={`nav-${labelKey}`}
        aria-current={active ? 'page' : undefined}
        aria-label={t(labelKey)}
      >
        <Icon size={22} strokeWidth={active ? 2.5 : 1.8} className="transition-all duration-200" />
        <span className="text-[9px] font-semibold leading-tight text-center">{t(labelKey)}</span>
      </motion.button>
    );
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 backdrop-blur-md border-t z-[10000] safe-bottom"
      style={{
        background: 'rgba(255, 247, 245, 0.92)',
        backdropFilter: 'blur(20px) saturate(140%)',
        WebkitBackdropFilter: 'blur(20px) saturate(140%)',
        borderTopColor: sectionTheme.border,
        boxShadow: '0 -8px 24px rgba(122,101,88,0.08)',
        transition: 'border-top-color 280ms var(--cb-ease-out)',
      }}
      data-testid="bottom-nav"
    >
      <div className="max-w-lg mx-auto grid grid-cols-5 items-end h-16 px-2 pb-2 gap-1">
        {sideItems.map(renderTab)}

        {/* FAB Scan central */}
        <div className="flex flex-col items-center justify-end pb-1">
          <motion.button
            onClick={() => handleNavigate('/scan')}
            whileTap={reducedMotion ? undefined : { scale: 0.94 }}
            transition={reducedMotion ? { duration: 0 } : CB_SPRING_SOFT}
            className="flex items-center justify-center"
            style={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              marginTop: -14,
              background: 'linear-gradient(135deg, var(--cb-peach-400), var(--cb-peach-600))',
              boxShadow: 'var(--cb-shadow-md)',
              color: 'var(--cb-text-on-primary)',
            }}
            data-testid="nav-scan"
            aria-label={t('scan')}
          >
            <Camera size={24} strokeWidth={2.2} />
          </motion.button>
          <span
            className="text-[10px] font-semibold leading-tight mt-1"
            style={{ color: 'var(--cb-peach-600)' }}
          >
            {t('scan')}
          </span>
        </div>

        {tailItems.map(renderTab)}
      </div>
    </nav>
  );
}

